import React, { useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { Contact } from '../types';
import { Icons } from '../constants';
import { db, auth } from '../firebase';
import { collection, addDoc, doc, updateDoc } from 'firebase/firestore';
import { handleFirestoreError, OperationType } from '../lib/firestore-errors';
import { logChange } from '../lib/changeLog';
import { Plus, Edit3, Trash2, Phone, Mail, MapPin, Search, X, Users } from 'lucide-react';

/**
 * Team — the people who work with GreenTree.
 *
 * Employees and contractors are stored as contacts with a personType, which is what keeps them
 * out of the customer list. This view is the other half of that: the place to add, edit and
 * remove them. Before it existed there was no way to put a new hire into the system at all.
 */

interface Props {
  contacts: Contact[];
  actingUser?: string;
}

const TEAM_TYPES = ['EMPLOYEE', 'CONTRACTOR'] as const;
type TeamType = typeof TEAM_TYPES[number];

const emptyForm = {
  name: '',
  personType: 'EMPLOYEE' as TeamType,
  phone: '',
  email: '',
  address: '',
  companyName: '',
  notes: '',
};

const TeamView: React.FC<Props> = ({ contacts, actingUser }) => {
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<Contact | null>(null);
  const [form, setForm] = useState({ ...emptyForm });
  const [confirmDelete, setConfirmDelete] = useState<Contact | null>(null);
  const [search, setSearch] = useState('');
  const [busy, setBusy] = useState(false);

  const team = contacts
    .filter(c => !(c as any).isDeleted && TEAM_TYPES.includes((c.personType || '') as TeamType))
    .filter(c => {
      if (!search.trim()) return true;
      const q = search.toLowerCase();
      return [c.name, c.email, c.phone, c.companyName].some(v => (v || '').toLowerCase().includes(q));
    })
    .sort((a, b) => a.name.localeCompare(b.name));

  const roleLabel = (t?: string) => (t === 'CONTRACTOR' ? 'Contractor' : 'Employee');

  const openAdd = () => {
    setEditing(null);
    setForm({ ...emptyForm });
    setShowForm(true);
  };

  const openEdit = (c: Contact) => {
    setEditing(c);
    setForm({
      name: c.name || '',
      personType: ((c.personType as TeamType) || 'EMPLOYEE'),
      phone: c.phone || '',
      email: c.email || '',
      address: c.address || '',
      companyName: c.companyName || '',
      notes: c.notes || '',
    });
    setShowForm(true);
  };

  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    if (busy) return;
    if (!form.name.trim()) {
      alert('Please give this person a name.');
      return;
    }
    setBusy(true);
    try {
      const payload = {
        name: form.name.trim(),
        personType: form.personType,
        phone: form.phone.trim(),
        email: form.email.trim(),
        address: form.address.trim(),
        companyName: form.companyName.trim(),
        notes: form.notes.trim(),
      };

      if (editing?.id) {
        await updateDoc(doc(db, 'contacts', editing.id), payload as any);
      } else {
        await addDoc(collection(db, 'contacts'), {
          ...payload,
          createdAt: new Date().toISOString(),
          userId: actingUser || 'System',
        } as any);
      }

      logChange({
        who: actingUser || 'System',
        action: editing ? 'UPDATE' : 'CREATE',
        entity: 'team member',
        entityId: editing?.id || form.name,
        entityLabel: form.name,
        summary: `${roleLabel(form.personType)} ${editing ? 'updated' : 'added'}`,
      });

      setShowForm(false);
      setEditing(null);
    } catch (error) {
      handleFirestoreError(error, OperationType.WRITE, 'contacts');
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!confirmDelete?.id) return;
    try {
      if (!auth.currentUser) throw new Error('Your session has expired. Please sign in again.');
      await auth.currentUser.getIdToken(true);
      await updateDoc(doc(db, 'contacts', confirmDelete.id), {
        isDeleted: true,
        deletedAt: new Date().toISOString(),
        deletedBy: actingUser || 'System',
      } as any);
      logChange({
        who: actingUser || 'System',
        action: 'DELETE',
        entity: 'team member',
        entityId: confirmDelete.id,
        entityLabel: confirmDelete.name,
        summary: `${roleLabel(confirmDelete.personType)} removed from the team`,
      });
      setConfirmDelete(null);
    } catch (error: any) {
      console.error(error);
      const message = error?.code === 'permission-denied'
        ? 'Remove was blocked: your session expired or you do not have permission. Sign in again and retry.'
        : (error?.message || 'Could not remove this person.');
      alert(message);
    }
  };

  const inputCls = 'w-full bg-stone-50 border border-stone-200 rounded-xl px-4 py-2.5 focus:ring-2 focus:ring-gecko outline-none transition text-sm font-bold text-bark';
  const labelCls = 'block text-[10px] font-black text-stone-400 uppercase tracking-wider mb-1';

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-2xl font-black text-bark flex items-center gap-2">
            <Users size={24} className="text-palm" /> Team
          </h2>
          <p className="text-stone-500 text-sm mt-1">
            Employees and contractors. Customers stay in Clients — this list is who works with you.
          </p>
        </div>
        <button
          onClick={openAdd}
          className="px-4 py-2.5 bg-gecko text-white rounded-xl font-bold text-sm hover:bg-palm transition flex items-center gap-2"
        >
          <Plus size={16} /> Add Person
        </button>
      </div>

      {/* Search */}
      <div className="relative max-w-sm">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
        <input
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Search the team..."
          className="w-full pl-9 pr-4 py-2.5 bg-white border border-stone-200 rounded-xl text-sm font-bold text-bark focus:ring-2 focus:ring-gecko outline-none"
        />
      </div>

      {/* List */}
      {team.length === 0 ? (
        <div className="bg-white rounded-2xl border border-stone-100 p-10 text-center">
          <p className="text-stone-400 font-bold">
            {search ? 'Nobody matches that search.' : 'No team members yet. Add the first one.'}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {team.map(person => (
            <motion.div
              key={person.id}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white rounded-2xl border border-stone-100 p-5 shadow-sm"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <h3 className="font-black text-bark truncate">{person.name}</h3>
                  <span className={`inline-block mt-1 px-2.5 py-0.5 rounded-full text-[10px] font-black uppercase tracking-wider ${
                    person.personType === 'CONTRACTOR' ? 'bg-blue-50 text-blue-600' : 'bg-gecko/10 text-gecko'
                  }`}>
                    {roleLabel(person.personType)}
                  </span>
                </div>
                <div className="flex gap-1 shrink-0">
                  <button onClick={() => openEdit(person)} title="Edit" className="p-2 rounded-lg text-stone-400 hover:text-palm hover:bg-stone-50 transition">
                    <Edit3 size={15} />
                  </button>
                  <button onClick={() => setConfirmDelete(person)} title="Remove" className="p-2 rounded-lg text-stone-400 hover:text-red-600 hover:bg-red-50 transition">
                    <Trash2 size={15} />
                  </button>
                </div>
              </div>

              <div className="mt-4 space-y-1.5 text-xs font-bold text-stone-500">
                {person.phone && <p className="flex items-center gap-2"><Phone size={13} className="text-stone-400" /> {person.phone}</p>}
                {person.email && <p className="flex items-center gap-2 truncate"><Mail size={13} className="text-stone-400" /> <span className="truncate">{person.email}</span></p>}
                {person.address && <p className="flex items-center gap-2"><MapPin size={13} className="text-stone-400" /> {person.address}</p>}
                {person.companyName && <p className="text-stone-400">{person.companyName}</p>}
              </div>

              {person.notes && (
                <p className="mt-3 pt-3 border-t border-stone-100 text-xs text-stone-500 leading-relaxed">{person.notes}</p>
              )}
            </motion.div>
          ))}
        </div>
      )}

      {/* Add / edit form */}
      <AnimatePresence>
        {showForm && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/50 backdrop-blur-sm p-4 pt-12 pb-12"
            onClick={e => { if (e.target === e.currentTarget) setShowForm(false); }}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.97 }}
              className="bg-white rounded-2xl shadow-2xl w-full max-w-xl overflow-hidden"
            >
              <div className="px-6 py-4 border-b border-stone-100 flex items-center justify-between">
                <h3 className="text-lg font-black text-bark">{editing ? 'Edit Person' : 'Add Person'}</h3>
                <button onClick={() => setShowForm(false)} className="p-2 rounded-lg text-stone-400 hover:text-stone-600 hover:bg-stone-50 transition">
                  <X size={18} />
                </button>
              </div>

              <form onSubmit={save} className="p-6 space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className={labelCls}>Name</label>
                    <input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} className={inputCls} placeholder="Full name" required />
                  </div>
                  <div>
                    <label className={labelCls}>They are a</label>
                    <select value={form.personType} onChange={e => setForm({ ...form, personType: e.target.value as TeamType })} className={inputCls}>
                      <option value="EMPLOYEE">Employee</option>
                      <option value="CONTRACTOR">Contractor</option>
                    </select>
                  </div>
                  <div>
                    <label className={labelCls}>Phone</label>
                    <input value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} className={inputCls} placeholder="(706) 555-0123" />
                  </div>
                  <div>
                    <label className={labelCls}>Email</label>
                    <input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} className={inputCls} placeholder="name@example.com" />
                  </div>
                </div>
                <div>
                  <label className={labelCls}>Address</label>
                  <input value={form.address} onChange={e => setForm({ ...form, address: e.target.value })} className={inputCls} placeholder="Street, city" />
                </div>
                <div>
                  <label className={labelCls}>Company (for contractors)</label>
                  <input value={form.companyName} onChange={e => setForm({ ...form, companyName: e.target.value })} className={inputCls} placeholder="Optional" />
                </div>
                <div>
                  <label className={labelCls}>Notes</label>
                  <textarea rows={3} value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} className={`${inputCls} resize-none`} placeholder="Certifications, crew role, availability, anything worth remembering" />
                </div>

                <div className="flex gap-3 pt-2">
                  <button type="button" onClick={() => setShowForm(false)} className="flex-1 py-3 border-2 border-stone-100 text-stone-500 font-bold rounded-xl hover:bg-stone-50 transition">
                    Cancel
                  </button>
                  <button type="submit" disabled={busy} className="flex-1 py-3 bg-gecko text-white font-bold rounded-xl hover:bg-palm transition disabled:opacity-60">
                    {busy ? 'Saving...' : editing ? 'Save Changes' : 'Add to Team'}
                  </button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Remove confirmation */}
      <AnimatePresence>
        {confirmDelete && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
          >
            <motion.div initial={{ scale: 0.97 }} animate={{ scale: 1 }} className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6">
              <h3 className="text-lg font-black text-bark mb-2">Remove {confirmDelete.name}?</h3>
              <p className="text-sm text-stone-500 mb-6">
                They come off the team list. The record stays in the change log and can be restored.
              </p>
              <div className="flex gap-3">
                <button onClick={() => setConfirmDelete(null)} className="flex-1 py-3 border-2 border-stone-100 text-stone-500 font-bold rounded-xl hover:bg-stone-50 transition">
                  Cancel
                </button>
                <button onClick={remove} className="flex-1 py-3 bg-red-500 text-white font-bold rounded-xl hover:bg-red-600 transition">
                  Remove
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default TeamView;
